/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { Button } from '@mui/material';
import { tb } from 'components/layouts/Table';
import { ExcpUserConditionSave } from 'models/admin/ExcpUser';
import { IconButton } from 'components/buttons/IconSVG';
import { changeExcpUserPwd, checkExcpUserIdDup, checkExcpUserIdPwd } from 'apis/admin/ExcpUser';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';

type Props = {
  open: boolean;
  close: () => void;
};
/**
 * @param open
 * @param close
 * @constructor
 */
const ChangePwdPopUp = ({ open, close }: Props) => {
  const { t } = useTranslation();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const [excpUserInfo, setExcpUserInfo] = useState<ExcpUserConditionSave>({
    excpId: '',
    userId: '',
    userNm: '',
    userPwd: '',
    userNewPwd: '',
    userEml: '',
    rmk: '',
  } as ExcpUserConditionSave);
  const [newPwd, setNewPwd] = useState<string>('');
  const [confirmNewPwd, setConfirmNewPwd] = useState<string>('');

  const [hasCompleteCheck, setCompleteCheck] = useState(false);

  useEffect(() => {
    initCondition();
  }, []);

  useEffect(() => {
    setCompleteCheck(false);
  }, [excpUserInfo?.userId]);

  const initCondition = async () => {
    setCompleteCheck(false);
  };

  const handleOnChangeUserId = (e) => {
    setExcpUserInfo({
      ...excpUserInfo,
      userId: e.target.value,
      userEml: e.target.value,
    });
  };

  const handleSave = async () => {
    const validatePwrd = (pwrd: any) => {
      return String(pwrd).match(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])[A-Za-z\d!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{8,16}$/
      );
    };

    if (!hasCompleteCheck) {
      openMessageBar({
        type: 'error',
        content: t('com.label.ID체크를 하신 후 진행 해주세요.', 'ID체크를 하신 후 진행 해주세요.'),
      });
      return;
    }
    if (!excpUserInfo?.userId) {
      openMessageBar({
        type: 'error',
        content: t('com.label.아이디를 입력 해주세요.', '아이디를 입력 해주세요.'),
      });
      return;
    }
    if (!excpUserInfo?.userPwd) {
      openMessageBar({
        type: 'error',
        content: t('com.label.비밀번호를 입력 해주세요.', '비밀번호를 입력 해주세요.'),
      });
      return;
    }
    if (!newPwd) {
      openMessageBar({
        type: 'error',
        content: t('com.label.새 비밀번호를 입력 해주세요.', '새 비밀번호를 입력 해주세요.'),
      });
      return;
    }

    if (!confirmNewPwd) {
      openMessageBar({
        type: 'error',
        content: t('com.label.비밀번호 확인을 입력 해주세요.', '비밀번호 확인을 입력 해주세요.'),
      });
      return;
    }

    if (excpUserInfo?.userPwd === newPwd) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.label.현재 패스워드와 동일한 패스워드로는 변경할 수 없습니다.',
          '현재 패스워드와 동일한 패스워드로는 변경할 수 없습니다.'
        ),
      });
      return;
    }

    if (validatePwrd(newPwd) == null) {
      openCommonModal({
        modalType: 'alert',
        content: t(
          'com.label.비밀번호는 8자이상 16자이하이어야 하고, 최소 하나의 숫자, 대문자, 소문자, 특수문자를 포함해야 됩니다.',
          '비밀번호는 8자이상 16자이하이어야 하고, 최소 하나의 숫자, 대문자, 소문자, 특수문자를 포함해야 됩니다.'
        ),
      });
      return;
    }

    if (newPwd !== confirmNewPwd) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.label.입력하신 비밀번호가 일치하지 않습니다.',
          '입력하신 비밀번호가 일치하지 않습니다.'
        ),
      });
      setExcpUserInfo({
        ...excpUserInfo,
        userNewPwd: '',
      });
      return;
    }

    const response = await checkExcpUserIdPwd(excpUserInfo?.userId, excpUserInfo?.userPwd);
    if (response.successOrNot === 'Y') {
      if (response.data.updatedRows == 8) {
        openCommonModal({
          content: '가입된 정보가 존재하지 않습니다. 아이디를 확인 하시길 바랍니다.',
        });
      } else if (response.data.updatedRows == 7) {
        openCommonModal({
          modalType: 'alert',
          content: '기존 비밀번호가 틀립니다.<br/>다시 한번 시도하여 주시기 바랍니다.',
        });
      } else {
        openCommonModal({
          modalType: 'confirm',
          content: t('com.label.비밀번호를 변경하시겠습니까?', '비밀번호를 변경하시겠습니까?'),
          yesCallback: async () => {
            const finalResponse = await changeExcpUserPwd({
              ...excpUserInfo,
              userNewPwd: newPwd,
            });
            if (finalResponse.data.updatedRows == 1) {
              openCommonModal({
                content: t(
                  'com.label.비밀번호를 변경했습니다.<br/>다시 로그인해주세요.',
                  '비밀번호를 변경했습니다.<br/>다시 로그인해주세요.'
                ),
              });
              close();
            } else {
              openCommonModal({
                content: '시스템 문제로 다시 한번 시도바랍니다.',
              });
            }
          },
        });
      }
    } else {
      openCommonModal({
        content: '시스템 문제로 다시 한번 시도바랍니다.',
      });
    }
  };

  const checkExistsUserId = async (e) => {
    if (!excpUserInfo?.userId) {
      openMessageBar({
        type: 'error',
        content: t('com.label.아이디를 입력 해주세요.', '아이디를 입력 해주세요.'),
      });
      return;
    }
    const response = await checkExcpUserIdDup(excpUserInfo?.userId);
    if (response == '0') {
      openMessageBar({
        type: 'error',
        content: t('com.label.존재하지 않는 ID 입니다.', '존재하지 않는 ID 입니다.'),
      });
      setCompleteCheck(false);
    } else {
      openCommonModal({
        modalType: 'alert',
        content: t(
          'com.label.사용자 인증이 완료되었습니다. 비밀번호를 변경하세요.',
          '사용자 인증이 완료되었습니다. 비밀번호를 변경하세요.'
        ),
      });
      setCompleteCheck(true);
    }
  };

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleSave}
      disableRipple
    >
      {t('com.button.저장', '저장')}
    </Button>,
  ];

  return (
    <>
      <CustomDialog
        title={t('com.label.비밀번호 변경', '비밀번호 변경')}
        size={'sm'}
        open={open}
        onClose={close}
        onCancel={close}
        buttons={dialogButtons}
      >
        <TableContainer css={tb.table}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>
                  <span>{t('com.label.아이디(이메일)', '아이디(이메일)')}</span>
                </TableCell>
                <TableCell className="duplication">
                  <div>
                    <CustomInputWithSearch
                      id="userId"
                      name="userId"
                      value={excpUserInfo.userId}
                      onChange={handleOnChangeUserId}
                      maxLength={50}
                      placeholder={String(
                        t('com.label.아이디(이메일)를 입력하세요.', '아이디(이메일)를 입력하세요.')
                      )}
                    />
                    <Button
                      onClick={checkExistsUserId}
                      disabled={hasCompleteCheck}
                      onChange={() => setCompleteCheck(false)}
                    >
                      {t('com.button.Check ID', 'Check ID')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span>{t('com.label.비밀번호', '비밀번호')}</span>
                </TableCell>
                <TableCell>
                  <CustomInputWithSearch
                    name="p"
                    value={excpUserInfo?.userPwd}
                    type="password"
                    onChange={(e) =>
                      setExcpUserInfo((prev) => ({ ...prev, userPwd: e.target.value }))
                    }
                    maxLength={20}
                    placeholder={String(
                      t('com.label.비밀번호를 입력해주세요.', '비밀번호를 입력해주세요.')
                    )}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span>{t('com.label.새 비밀번호', '새 비밀번호')}</span>
                </TableCell>
                <TableCell>
                  <CustomInputWithSearch
                    name="upd"
                    value={newPwd}
                    type="password"
                    onChange={(e) => setNewPwd(e.target.value)}
                    maxLength={20}
                    placeholder={String(
                      t('com.label.새 비밀번호를 입력해주세요.', '새 비밀번호를 입력해주세요.')
                    )}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <span>{t('com.label.비밀번호 확인', '비밀번호 확인')}</span>
                </TableCell>
                <TableCell>
                  <CustomInputWithSearch
                    name="confirmNewPwd"
                    value={confirmNewPwd}
                    type="password"
                    onChange={(e) => setConfirmNewPwd(e.target.value)}
                    maxLength={20}
                    placeholder={String(
                      t('com.label.비밀번호를 다시 입력하세요.', '비밀번호를 다시 입력하세요.')
                    )}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CustomDialog>
    </>
  );
};

export default ChangePwdPopUp;
